// Capture / integrate live-data preview for an embedded transaction.
//
// Brings capture and integrate mode up to parity with the realtime list
// embed (live-preview): a row list at the top with filter+retrieve, a
// "+ Add" button that seeds the new record from the active filter / args
// / server defaults, an editable form for the selected row, batch Save
// All across every unsaved/dirty record, and per-row status badges
// (retrieved → dirty after edit, unsaved → saved after batch save).
//
// Persistence and validation go through the live-preview executor via
// `useEmbedRecords` + livePreviewProcess, so capture/integrate gets the
// same DB-side checks, audit columns, and project-variable defaults
// live-preview already provides.
//
// Note on "offline": records are held in local React state until the
// user clicks Save All. If the network round-trip fails, the records
// stay in `unsaved`/`dirty`/`failed` so the user can retry. A
// future-iteration IndexedDB queue would persist them across reloads;
// the present in-memory store covers the common "edit several rows,
// then save" pattern.

import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertCircle, FilePlus, Loader2, Pencil, RefreshCw, Save, Trash2, X,
} from 'lucide-react'

import { applyActionResultViaOnChange, type CaptureRuntime } from './contract'
import type { DataAccess } from '../../dataBinding'
import {
  allSqlModels, buildParentPk, childFormsOf, columnDbName, columnFormKey,
  ddlbDisplayLabel,
  gridOrderedColumns, headerFormNo, mainSqlModel, parseApiError,
  richCellText, listHeadingOf,
} from '../utils'
import ChildFormTab, { withLocalId, type ChildStore } from '../screen/ChildFormTab'
import { PeopleProvider, personLabel, usePeople } from '../people'
import { TxnScreenRuntimeProvider } from '../screen/screenContext'
import type { TxnScreenRuntime } from '../screen/contract'
import {
  colKeyOf, layoutIsDirty, orderedDisplayCols, widthOf,
  useColumnDnd, useResolvedGridLayout, RESIZE_HANDLE_STYLE, type GridLayoutApi,
} from '../list/columnLayout'
import { useGridLayout } from '../list/useGridLayout'
import { LayoutActions } from '../list/LayoutActions'
import CoreFormRenderer, { fieldAnchorId } from '../FormRenderer'
import CompactRecordCard from '../compact/CompactRecordCard'
import CompactFormShell from '../compact/CompactFormShell'
import FormTabs from '../list/FormTabs'
import ListToolbar from '../list/ListToolbar'
import { useEnforcedFilterParts } from '../list/EnforcedFilterControls'
import { CompactSummaryCard } from '../compact/CompactRecordCard'
import { tw } from '../../testHooks'
import RowActionsMenu from '../list/RowActionsMenu'
import {
  TxnRuntimeProvider, useTxnRuntime, type TxnRuntime,
} from '../context'
import {
  useEmbedRecords, type EmbedRecord,
} from './useEmbedRecords'
import { useWarningConfirm } from '../WarningsConfirmModal'
import { useItemChange, applyRowEvent, applySeededProtect } from '../list/useItemChange'
import type { RecordStatus } from '../contracts'

import type { ColumnDef, ItemChangeFormChange, SqlModel, TransactionModel } from '../types'
import { formLinesViewOnly } from '../types'
import type { EmbedConfig, PageInput } from '@twasta/page-schema'
import { applyFormRowsChange, formChangeTouchesRows, listAllChildRows } from '../screen/detailsBuffer'

interface Props {
  /** Injected host runtime: data adapter + form-stack transport/notifier +
   *  layout persistence + optional ensureReady (deploy). See contract.ts. */
  runtime: CaptureRuntime
  /** Bump to force a model reload — replaces the platform's object-version
   *  store subscription (the designer shim passes `useObjectVersion(...)`). */
  versionKey?: unknown
  project: string
  embed: EmbedConfig
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  /** Site-mode routing for model + CRUD. */
  dataAccess?: DataAccess
  /** Called when a row is selected in the list. The designer wires
   *  this to setInputValues so a `selectedIdVar` / `selectedDataVar`
   *  drive downstream embeds + variable display the same way live-
   *  preview does. */
  onSetInput?: (name: string, value: unknown) => void
  /** Fired after a successful batch save (modal presentation uses it to
   *  confirm and auto-close). */
  onSaved?: () => void
  /**
   * Runtime interface switcher rendered on the right of the toolbar row.
   * Set when this list+form is used as an alternate view of a realtime
   * list embed (EmbedListInterfaceHost); unset in the normal capture path.
   */
  viewSwitcher?: React.ReactNode
  // Access gating (parity with the sibling List view's rights). Default true so
  // the DESIGNER — which passes nothing — stays fully enabled. A read-only
  // runtime viewer passes canAdd=false so "Add" / "Save All" writes it would
  // 403 on never appear. The server stays the authoritative gate.
  canAdd?: boolean
  canEdit?: boolean
  /** Presentation. 'default' is the list + popup-form desktop layout;
   *  'compact' is the mobile-style card list + full-screen single-column form
   *  (the 'multi-compact' capture template); 'bulk' is a transaction screen's
   *  Bulk entry — the edit set as compact cards beside the selected record in
   *  the record editor (every form). How it WORKS is the same in all three:
   *  Apply keeps a record in the edit set, Save All writes them. */
  variant?: 'default' | 'compact' | 'bulk'
  /** 'bulk': the record editor's layout and the switch that changes it (the
   *  screen remembers the choice). Document is never offered here — it
   *  writes at once, and an edit set holds edits until Save All. */
  editorLayout?: 'standard' | 'simple' | 'compact'
  editorLayoutSwitch?: React.ReactNode
  /** 'bulk': the screen's own toolbar pieces, as on the Spread sheet —
   *  `beforeAddActions` before Add (the approvals button, actions placed on
   *  the bar) and `headerActions` after Save All (the screen's ⋮ menu). */
  beforeAddActions?: React.ReactNode
  headerActions?: React.ReactNode
  /** The list tab the screen is on (`list_view.tabs`) — the edit set is
   *  pulled from the same records the tab shows. */
  listTab?: string | null
  /** 'bulk': the screen's search scope, shared with the List (the same set /
   *  overrides; the edit set is pulled under it). */
  enforcedOverrides?: Record<string, { from?: string; to?: string; value?: string }>
  filterSetId?: string
  bypassEnforced?: boolean
  onApplyOverrides?: (overrides: Record<string, { from?: string; to?: string; value?: string }>) => void
  onSelectFilterSet?: (sel: { setId?: string; bypass?: boolean }) => void
}

// Local twin of the platform's designer FormRenderer wrapper: folds the
// per-call uploadDocument / runAction overrides into the ambient TxnRuntime
// (exactly what makeDesignerTxnRuntime(project, uploadDocument, runAction)
// did) so the core form + its LookupModal see the right transport.
function CaptureFormRenderer({
  uploadDocument, runAction, ...rest
}: ComponentProps<typeof CoreFormRenderer> & {
  uploadDocument?: TxnRuntime['uploadDocument']
  runAction?: TxnRuntime['runAction']
}) {
  const base = useTxnRuntime()
  const value = useMemo<TxnRuntime>(() => ({
    ...base,
    ...(uploadDocument ? { uploadDocument } : {}),
    ...(runAction ? { runAction } : {}),
  }), [base, uploadDocument, runAction])
  return (
    <TxnRuntimeProvider value={value}>
      <CoreFormRenderer {...rest} />
    </TxnRuntimeProvider>
  )
}

// ── Status presentation ─────────────────────────────────────────────

const STATUS_LABEL: Record<RecordStatus, string> = {
  retrieved: 'unchanged',
  dirty: 'edited',
  unsaved: 'new',
  saved: 'saved',
  failed: 'failed',
  partial: 'partial',
}

const STATUS_BG: Record<RecordStatus, string> = {
  retrieved: 'rgba(100,116,139,0.18)',  // slate
  dirty: 'rgba(234,179,8,0.20)',        // amber
  unsaved: 'rgba(59,130,246,0.18)',     // blue
  saved: 'rgba(16,185,129,0.20)',       // green
  failed: 'rgba(239,68,68,0.20)',       // red
  partial: 'rgba(249,115,22,0.20)',     // orange
}

const STATUS_FG: Record<RecordStatus, string> = {
  retrieved: '#475569',
  dirty: '#a16207',
  unsaved: '#1d4ed8',
  saved: '#047857',
  failed: '#b91c1c',
  partial: '#c2410c',
}

// Inline sync indicator for the header banner. Shows a single "in sync"
// chip when nothing is pending, and a per-status breakdown otherwise.
function SyncStatus({
  inSync, counts,
}: {
  inSync: boolean
  counts: Record<RecordStatus, number>
}) {
  if (inSync) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 10,
        fontSize: '0.8125rem', fontWeight: 600,
        background: 'rgba(16,185,129,0.18)', color: '#047857',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }} title="Local state matches the server">
        ● in sync
      </span>
    )
  }
  return (
    <>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 10,
        fontSize: '0.8125rem', fontWeight: 600,
        background: 'rgba(234,179,8,0.18)', color: '#a16207',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }} title="Local edits have not been pushed to the server">
        ● not synced
      </span>
      {counts.retrieved > 0 && (
        <span title="Loaded from server, unchanged">· {counts.retrieved} retrieved</span>
      )}
      {counts.dirty > 0 && (
        <span style={{ color: '#a16207' }}>· {counts.dirty} edited</span>
      )}
      {counts.unsaved > 0 && (
        <span style={{ color: '#1d4ed8' }}>· {counts.unsaved} new</span>
      )}
      {counts.failed > 0 && (
        <span style={{ color: '#b91c1c' }}>· {counts.failed} failed</span>
      )}
      {counts.partial > 0 && (
        <span style={{ color: '#c2410c' }}>· {counts.partial} partial</span>
      )}
    </>
  )
}

function StatusBadge({ status }: { status: RecordStatus }) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 10,
      fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.04em',
      background: STATUS_BG[status],
      color: STATUS_FG[status],
    }}>
      {STATUS_LABEL[status]}
    </span>
  )
}

// ── Component ───────────────────────────────────────────────────────

export default function EmbedCapturePreview({
  runtime, versionKey,
  project, embed, inputs: _inputs, inputValues, onSetInput, onSaved,
  dataAccess, viewSwitcher, canAdd = true, canEdit = true, variant = 'default',
  beforeAddActions, headerActions,
  editorLayout = 'simple', editorLayoutSwitch, listTab,
  enforcedOverrides, filterSetId, bypassEnforced, onApplyOverrides, onSelectFilterSet,
}: Props) {
  const compact = variant === 'compact'
  const bulk = variant === 'bulk'
  const objName = embed.obj_name
  const { api, txn } = runtime
  const notify = txn.notify
  // Host-supplied freshness key (the designer shim binds the object-version
  // store); a change re-triggers the model reload exactly as the store
  // subscription did.
  const txnVersion = versionKey

  // Transaction model (column metadata + sql_models) ────
  const [model, setModel] = useState<TransactionModel | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)
  const [modelLoading, setModelLoading] = useState(false)

  useEffect(() => {
    if (!project || !objName) {
      setModel(null)
      setModelError(null)
      return
    }
    let cancelled = false
    setModelLoading(true)
    setModelError(null)
    api.loadModel(dataAccess, project, objName)
      .then((res) => {
        if (cancelled) return
        const content = res?.content
        if (!content) {
          setModel(null)
          setModelError(`Transaction "${objName}" not yet generated`)
          return
        }
        const m = content.transaction || content
        setModel(m as TransactionModel)
      })
      .catch(() => {
        if (cancelled) return
        setModelError('Failed to load transaction')
      })
      .finally(() => { if (!cancelled) setModelLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, txnVersion, dataAccess])

  const sm = useMemo(() => (model ? mainSqlModel(model) : null), [model])

  // Live column layout (drag to resize / reorder + author apply-to-design).
  const canEditDesign = (dataAccess?.mode ?? '') !== 'site'
  const mainFormNo = useMemo(
    () => (model ? (allSqlModels(model)[0]?.formNo ?? '') : ''),
    [model],
  )
  const gridLayout = useGridLayout({
    model, setModel, project, objName: objName || '', canEdit: canEditDesign,
    // 'customize' (Run view / deployed runtime) writes a per-tenant overlay;
    // 'design' (the designer) bakes into the shared core design.
    mode: runtime.layoutMode ?? 'design',
    notify, persistence: runtime.layout,
  })

  // Deploy + record state ───────────────────────────────
  const [deployState, setDeployState] = useState<
    'idle' | 'deploying' | 'deployed' | 'error'
  >('idle')
  const [deployError, setDeployError] = useState<string | null>(null)

  // "Ignore warnings and proceed" confirm for business-logic warnings on save.
  const { confirmWarnings, warningsModal } = useWarningConfirm()
  const records = useEmbedRecords({
    api,
    notify,
    dataAccess,
    project,
    objName: objName || '',
    inputValues,
    filters: embed.filters || [],
    args: embed.args || [],
    recordVar: embed.recordVar,
    sm,
    listTab,
    ...(bulk ? { enforcedOverrides, filterSetId, bypassEnforced } : {}),
    confirmWarnings,
  })

  // Auto-refetch when the embed filter's source variables change —
  // matches the realtime list embed's behaviour so chained drill-down
  // (parent list selection drives child capture list filter) works
  // without the user having to hit Retrieve manually. We snapshot the
  // resolved filter values into a string signature so referentially-
  // distinct but value-equal filter arrays don't re-fire.
  const filtersSig = useMemo(() => {
    const fs = embed.filters || []
    return JSON.stringify(fs.map((f) => {
      const ref = f.value
      let val: unknown = undefined
      if (ref?.source === 'page_input' && ref.name) {
        val = inputValues[ref.name] ?? ''
      } else if (ref) {
        val = ref.value ?? ''
      }
      let fromVal: unknown = undefined
      let toVal: unknown = undefined
      if (f.op === 'between') {
        if (f.from?.source === 'page_input' && f.from.name) {
          fromVal = inputValues[f.from.name] ?? ''
        } else if (f.from) fromVal = f.from.value ?? ''
        if (f.to?.source === 'page_input' && f.to.name) {
          toVal = inputValues[f.to.name] ?? ''
        } else if (f.to) toVal = f.to.value ?? ''
      }
      return { c: f.column, o: f.op, v: val, f: fromVal, t: toVal }
    }))
  }, [embed.filters, inputValues])
  // A tab or scope change pulls those records (same in-sync rule as a filter).
  const fetchSig = `${filtersSig}|${listTab || ''}|${JSON.stringify({ o: enforcedOverrides || {}, s: filterSetId || '', b: !!bypassEnforced })}`

  const initialRetrieveDoneRef = useRef(false)
  useEffect(() => {
    // Skip the very first effect run; the deploy useEffect below handles
    // the initial retrieve after the schema is deployed.
    if (!initialRetrieveDoneRef.current) {
      initialRetrieveDoneRef.current = true
      return
    }
    if (deployState !== 'deployed') return
    // Auto-refetch on filter change ONLY when local state is in sync.
    // Pending edits block the refetch — the out-of-sync banner tells
    // the user to Save All or Discard first.
    if (records.hasPendingChanges) return
    records.retrieve().catch(() => { /* surfaced via lastSaveError */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSig, deployState])

  // Deploy once per (project, obj_name) per session, then trigger the
  // initial retrieve so the user sees existing rows. We don't auto-
  // retrieve on every filter change — capture/integrate is meant for
  // intentional editing sessions, not live drilldown. Use the
  // explicit Retrieve button.
  useEffect(() => {
    if (!project || !objName) return
    let cancelled = false
    // Site mode (public end-user page): schema already deployed by the author;
    // site users must not call the author-only deploy endpoint (it 401s).
    if (dataAccess?.mode === 'site') {
      setDeployState('deployed')
      void records.retrieve().catch(() => { /* non-fatal */ })
      return () => { cancelled = true }
    }
    setDeployState('deploying')
    setDeployError(null)
    // The host prepares the backing store (the designer shim deploys the
    // live-preview schema, idempotently across remounts); a host that is
    // always deployed (standalone runtime) omits ensureReady.
    ;(runtime.ensureReady
      ? runtime.ensureReady(project, objName)
      : Promise.resolve())
      .then(async () => {
        if (cancelled) return
        setDeployState('deployed')
        try {
          await records.retrieve()
        } catch {
          // Initial retrieve failure isn't fatal — user can hit
          // Retrieve again. We don't log to toast on first load
          // because the embed deploy might be visible mid-init.
        }
      })
      .catch((err) => {
        if (cancelled) return
        const { message } = parseApiError(err)
        setDeployError(message)
        setDeployState('error')
      })
    return () => { cancelled = true }
    // We intentionally don't depend on `records.retrieve` (its identity
    // changes on every render) — initial retrieve fires once per
    // (project, objName).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, dataAccess])

  // Selection plumbing (drive selectedIdVar / selectedDataVar) ──
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const lastSelectionRef = useRef<{ id: string | null; data: unknown }>({
    id: null, data: null,
  })

  const writeSelectionVars = useCallback((rec: EmbedRecord | null) => {
    if (!onSetInput) return
    const pk = rec?.primaryKey ?? null
    const last = lastSelectionRef.current
    if (embed.selectedIdVar && last.id !== pk) {
      onSetInput(embed.selectedIdVar, pk ?? '')
    }
    if (embed.selectedDataVar && last.data !== rec) {
      onSetInput(embed.selectedDataVar, rec ? rec.header : undefined)
    }
    lastSelectionRef.current = { id: pk, data: rec }
  }, [embed.selectedIdVar, embed.selectedDataVar, onSetInput])

  const handleSelect = useCallback((rec: EmbedRecord) => {
    setSelectedId(rec.id)
    writeSelectionVars(rec)
  }, [writeSelectionVars])

  // Restore selection from `inputValues[embed.selectedIdVar]` when
  // that variable carries a value the embed didn't write itself —
  // typically a smart document being reopened, where saved vars
  // rehydrate before the embed's records load. Matches against
  // `primaryKey` since that's what the var holds. Seeds
  // `lastSelectionRef` so writeSelectionVars treats the echo as a
  // no-op and doesn't pointlessly round-trip.
  useEffect(() => {
    const varName = embed.selectedIdVar
    if (!varName) return
    const raw = inputValues[varName]
    const targetPk = raw == null || raw === '' ? null : String(raw)
    const currentRec = selectedId ? records.records.find((r) => r.id === selectedId) || null : null
    const currentPk = currentRec?.primaryKey ?? null
    if (targetPk === currentPk) return
    if (targetPk == null) {
      setSelectedId(null)
      lastSelectionRef.current = { id: null, data: null }
      return
    }
    const rec = records.records.find((r) => r.primaryKey === targetPk)
    if (!rec) return
    setSelectedId(rec.id)
    lastSelectionRef.current = { id: targetPk, data: rec }
  }, [embed.selectedIdVar, inputValues, records.records, selectedId])

  // Editor modal ───────────────────────────────────────
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  // Tracks the row currently in the editor so an async row_edit cascade can
  // bail if the user switched rows before it resolved.
  const editingIdRef = useRef<string | null>(null)
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [formDirty, setFormDirty] = useState(false)
  // 'bulk': which records the cards show, and the Standard layout's form tab.
  const [bulkFilter, setBulkFilter] = useState<BulkFilter>('all')
  const [bulkFormTab, setBulkFormTab] = useState<string>('')
  const [bulkSearch, setBulkSearch] = useState('')
  // The scope's pieces for the shared toolbar row (bulk only shows them).
  const bulkScope = useEnforcedFilterParts({
    model: sm || ({ columns: [] } as unknown as SqlModel),
    enforcedFilters: records.enforcedFilters, filterSets: records.filterSets,
    activeFilterSetId: records.activeFilterSetId, bypassed: records.bypassed,
    busy: records.busy, notices: records.filterNotices,
    onApplyFilters: onApplyOverrides, onSelectFilterSet, project, objName: objName || '',
  })
  // Per-column protect overrides returned by an item_change ({col:{protect:1|0}}),
  // applied to the open form. Reset whenever a different record opens.
  const [dynamicProtect, setDynamicProtect] = useState<Record<string, 0 | 1>>({})

  // Pre-derive the currently-editing record so the modal's title /
  // status / error banner reflect the latest state if Save All
  // updates it from underneath.
  const editingRecord = useMemo(() => {
    if (!editingId) return null
    return records.records.find((r) => r.id === editingId) || null
  }, [editingId, records.records])

  // ── Compact child sections (Phase 7) ──────────────────────────────────────
  // In the compact editor the header form's DIRECT child forms render as
  // stacked compact sections (mobile-style card lists + full-screen row editor)
  // backed by the record's `details` buffer. Reuses the shared ChildFormTab —
  // so item_change / validation / grandchildren all work — via a minimal
  // TxnScreenRuntime adapter over this embed's CaptureRuntime.
  const detailForms = useMemo(
    () => ((compact || bulk) && model ? childFormsOf(model, headerFormNo(model)) : []),
    [compact, bulk, model],
  )
  // The parent key the child sections scope their lazy-load by = the edited
  // header's own key values. Stable across non-key keystrokes via the JSON key.
  const childParentPk = useMemo(
    () => (sm && editingRecord ? buildParentPk(sm, editingRecord.header) : {}),
    [sm, editingRecord],
  )
  const childParentPkKey = useMemo(() => JSON.stringify(childParentPk), [childParentPk])
  // Adapter exposing just what ChildFormTab reads from the screen runtime
  // (txn transport + execute funnel for item_change/validate + list for lazy
  // child loads). The write/approvals surface is unused here — the whole record
  // tree persists through the embed's own batch Save All — so those stubs throw.
  const editingRecordId = editingRecord?.id
  const editingPrimaryKey = editingRecord?.primaryKey
  const screenRuntimeAdapter = useMemo<TxnScreenRuntime>(() => ({
    txn,
    execute: (obj, action, payload) => api.execute(dataAccess, project, obj, action, payload),
    list: (obj, q) => api.list(
      dataAccess, project, obj, q.limit, q.offset, q.formNo, q.parentPk,
      q.filters, q.enforcedOverrides,
      (q.filterSetId != null || q.bypass != null)
        ? { filterSetId: q.filterSetId, bypass: q.bypass }
        : undefined,
    ),
    load: () => Promise.reject(new Error('load is not supported in the embed child editor')),
    saveBatch: () => Promise.reject(new Error('saveBatch is not supported in the embed child editor')),
    remove: () => Promise.reject(new Error('remove is not supported in the embed child editor')),
    resolveDefaults: () => Promise.resolve({ ok: true, values: {} }),
    recordHistory: () => Promise.reject(new Error('recordHistory is not supported in the embed child editor')),
    approvals: { list: () => Promise.reject(new Error('approvals are not supported in the embed child editor')) },
  }), [txn, api, dataAccess, project])

  // A ChildStore over the edited record's `details[childFormNo]` buffer. Add /
  // edit / remove mutate it through useEmbedRecords (auto-flips the record to
  // dirty); ensureLoaded lazily pulls existing child rows for a SAVED record.
  const makeEmbedChildStore = (childFormNo: string): ChildStore => ({
    rows: (editingRecord?.details[childFormNo] as Array<Record<string, unknown>>) || [],
    add: (row) => {
      if (!editingRecordId) return
      records.updateDetailRows(editingRecordId, childFormNo, (prev) => [...prev, withLocalId(row)])
    },
    edit: (index, patch) => {
      if (!editingRecordId) return
      records.updateDetailRows(editingRecordId, childFormNo, (prev) => {
        const rows = prev.slice()
        const target = rows[index]
        if (!target) return prev
        const { _error, _fieldErrors, ...rest } = target as Record<string, unknown>
        void _error; void _fieldErrors
        rows[index] = { ...rest, ...patch }
        return rows
      })
    },
    remove: (index) => {
      if (!editingRecordId) return
      records.updateDetailRows(editingRecordId, childFormNo, (prev) => {
        const target = prev[index]
        if (!target) return prev
        // A saved row is flagged deleted (so the batch removes it); a never-saved
        // row just drops out of the buffer.
        return target._txn_id
          ? prev.map((r, i) => (i === index ? { ...r, _deleted: true } : r))
          : prev.filter((_, i) => i !== index)
      })
    },
    ensureLoaded: async (parentPk) => {
      // A brand-new (unsaved) record has no server-side children yet.
      if (!editingRecordId || !editingPrimaryKey) return
      if (!parentPk || Object.keys(parentPk).length === 0) return
      if (editingRecord?.details[childFormNo] !== undefined) return
      try {
        const rows = await listAllChildRows(
          (q) => api.list(dataAccess, project, objName || '', q.limit, q.offset, q.formNo, q.parentPk),
          childFormNo, parentPk,
        )
        records.seedDetails(editingRecordId, childFormNo, rows)
      } catch {
        records.seedDetails(editingRecordId, childFormNo, [])
      }
    },
  })

  const startAdd = useCallback(async () => {
    if (!sm) return
    setEditingId(null)
    try {
      const rec = await records.addNew()
      setEditingId(rec.id)
      editingIdRef.current = rec.id
      setFormValues({ ...rec.header })
      // Seed from the add-cascade's protect so a conditionally-disabled
      // column (e.g. driven by a defaulted radio) opens already locked.
      setDynamicProtect(rec._cascadeProtect || {})
      setFormDirty(false)
      setEditorOpen(true)
      setSelectedId(rec.id)
    } catch (err) {
      const { message } = parseApiError(err)
      notify.error(`Could not add new record: ${message}`)
    }
  }, [sm, records, notify])

  const startEdit = useCallback((id: string) => {
    const rec = records.records.find((r) => r.id === id)
    if (!rec) return
    setEditingId(id)
    editingIdRef.current = id
    setFormValues({ ...rec.header })
    setDynamicProtect({})
    setFormDirty(false)
    setEditorOpen(true)
    setSelectedId(id)
    writeSelectionVars(rec)
    // Form-level row_edit event: recompute / refresh values when an existing
    // row is opened for edit (the form-level cousin of item_change). The
    // derived values become unsaved edits so they persist on Apply/Save.
    // Best-effort and guarded against the user switching rows mid-flight.
    if (project && objName && sm) {
      const base = { ...rec.header }
      const mergeProtect = (protect?: Record<string, 0 | 1>) => {
        if (!protect || !Object.keys(protect).length) return
        if (editingIdRef.current !== id) return
        setDynamicProtect((prev) => ({ ...prev, ...protect }))
      }
      applyRowEvent(
        api.execute, dataAccess, project, objName, sm, 'row_edit', base,
        undefined, undefined, (res) => mergeProtect(res.protect),
      )
        .then((next) => {
          if (editingIdRef.current !== id) return
          const changed = Object.keys(next).some((k) => next[k] !== base[k])
          if (!changed) return
          setFormValues((prev) => ({ ...prev, ...next }))
          setFormDirty(true)
        })
        .catch(() => { /* best-effort: keep editing without the cascade */ })
      // Declarative protect rules live on column item_changes, which only
      // fire on a live change — evaluate them once for the record's current
      // values so a conditionally-disabled column opens disabled.
      applySeededProtect(api.execute, dataAccess, project, objName, sm, base)
        .then(mergeProtect)
        .catch(() => { /* best-effort: keep editing without the lock state */ })
    }
  }, [records.records, writeSelectionVars, project, objName, sm, dataAccess, api])

  // Bulk entry keeps a record open beside the edit set — the first one until
  // the person picks another. A re-read of the set (deploy, tab or scope
  // change) replaces the records, so the open one is re-picked when it has
  // gone. Once the person closes the editor (Cancel / Apply) it stays closed.
  const userClosedRef = useRef(false)
  useEffect(() => {
    if (!bulk || userClosedRef.current || records.busy || deployState !== 'deployed') return
    if (editingId && records.records.some((r) => r.id === editingId)) return
    const first = records.records[0]
    if (first) startEdit(first.id)
  }, [bulk, records.records, records.busy, deployState, editingId, startEdit])

  const closeEditor = useCallback(() => {
    if (formDirty && !window.confirm('You have unsaved field edits. Discard?')) return
    userClosedRef.current = true
    setEditorOpen(false)
    setEditingId(null)
    editingIdRef.current = null
    setFormValues({})
    setDynamicProtect({})
    setFormDirty(false)
  }, [formDirty])

  // Apply edits to the underlying record on Apply / on close.
  const applyFormToRecord = useCallback(() => {
    if (!editingId) return
    records.updateHeader(editingId, formValues)
    setFormDirty(false)
  }, [editingId, formValues, records])

  // Best-effort item_change cascade for popup-form edits. Server-side
  // failure (endpoint missing, network down, deployed function not
  // present) is swallowed in the hook — the user can keep editing.
  const triggerItemChange = useItemChange<{ id: string | null }>({
    execute: api.execute,
    dataAccess,
    project, objName: objName || '', sm,
    onUpdates: (ctx, updates, result) => {
      // Current (header) form values.
      setFormValues((prev) => ({ ...prev, ...updates }))
      if (ctx.id) records.updateHeader(ctx.id, updates)
      // Dynamic protect for the open form (1 protect / 0 unprotect).
      const headerChange = result?.forms?.[mainFormNo]
      const protect = result?.protect || headerChange?.protect
      if (protect && Object.keys(protect).length) {
        setDynamicProtect((prev) => ({ ...prev, ...protect }))
      }
      // Other forms the return targeted (child rows / header set). Persisted on
      // the record so a batch save keeps them, even though this single-form
      // popup doesn't render child grids.
      if (result?.forms && ctx.id) {
        for (const [fno, change] of Object.entries(result.forms)) {
          if (fno === mainFormNo) {
            if (change.set && Object.keys(change.set).length) {
              setFormValues((prev) => ({ ...prev, ...change.set }))
              records.updateHeader(ctx.id, change.set)
            }
          } else if (formChangeTouchesRows(change)) {
            // appended, or (replace) swapped for the form's existing rows
            records.updateDetailRows(ctx.id, fno, (prev) =>
              applyFormRowsChange(prev, change, withLocalId))
          }
        }
      }
    },
  })

  // A line's row event (row_complete…) returned values for the header form.
  const applyHeaderPatch = useCallback((patch: Record<string, ItemChangeFormChange>) => {
    const set = patch[mainFormNo]?.set
    if (!set || Object.keys(set).length === 0) return
    setFormValues((prev) => ({ ...prev, ...set }))
    if (editingId) records.updateHeader(editingId, set)
    setFormDirty(true)
  }, [mainFormNo, editingId, records])

  const onFieldChange = useCallback((name: string, v: unknown) => {
    setFormValues((prev) => {
      const next = { ...prev, [name]: v }
      triggerItemChange({ id: editingId }, name, next)
      return next
    })
    setFormDirty(true)
  }, [editingId, triggerItemChange])

  // Save All ───────────────────────────────────────────
  const handleSaveAll = useCallback(async () => {
    // Push any in-flight form edits into the underlying record first
    // so the form's modal values don't get lost on save.
    if (editingId && formDirty) applyFormToRecord()
    const outcome = await records.saveBatch()
    if (outcome.success) {
      notify.success('All records saved')
      onSaved?.()
    } else if (outcome.failedCount > 0) {
      notify.error(`${outcome.failedCount} record${outcome.failedCount === 1 ? '' : 's'} failed validation`)
    }
  }, [records, editingId, formDirty, applyFormToRecord, onSaved, notify])

  const handleRetrieve = useCallback(async () => {
    // Guarded by the button's disabled state, but defended here too —
    // retrieve is only valid when local state is in sync with the
    // server.
    if (records.hasPendingChanges) return
    try {
      await records.retrieve()
    } catch (err) {
      const { message } = parseApiError(err)
      notify.error(`Refresh failed: ${message}`)
    }
  }, [records, notify])

  const handleDiscardChanges = useCallback(async () => {
    const pending = records.counts.unsaved + records.counts.dirty +
      records.counts.failed + records.counts.partial
    if (!window.confirm(
      `Discard ${pending} pending change${pending === 1 ? '' : 's'} and reload from the server?`,
    )) return
    if (editingId) {
      setEditorOpen(false)
      setEditingId(null)
      setFormValues({})
      setFormDirty(false)
    }
    records.discardLocalEdits()
    try {
      await records.retrieve()
    } catch (err) {
      const { message } = parseApiError(err)
      notify.error(`Refresh failed: ${message}`)
    }
  }, [records, editingId, notify])

  // Remove a row from local state (capture/integrate doesn't persist
  // a delete server-side from this surface — that's a separate
  // explicit action, intentionally not exposed here).
  const handleRemoveLocal = useCallback((id: string) => {
    const rec = records.records.find((r) => r.id === id)
    if (!rec) return
    const msg = rec.status === 'unsaved'
      ? 'Remove this unsaved record?'
      : `This will drop "${rec.primaryKey || ''}" from the local edit set (the server copy stays). Continue?`
    if (!window.confirm(msg)) return
    if (editingId === id) {
      setEditorOpen(false)
      setEditingId(null)
      setFormDirty(false)
    }
    if (selectedId === id) {
      setSelectedId(null)
      writeSelectionVars(null)
    }
    records.removeLocal(id)
  }, [records, editingId, selectedId, writeSelectionVars])

  // ── Render ─────────────────────────────────────────

  if (!objName) {
    return (
      <div style={emptyStyle}>
        <AlertCircle size={18} />
        <span>Pick a transaction in the property panel to embed.</span>
      </div>
    )
  }
  if (modelLoading) {
    return (
      <div style={emptyStyle}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading transaction model…</span>
      </div>
    )
  }
  if (modelError) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{modelError}</span>
      </div>
    )
  }
  if (!sm) return null

  if (bulk) {
    const pendingCount = records.counts.unsaved + records.counts.dirty
      + records.counts.failed + records.counts.partial
    const ready = deployState === 'deployed'
    const q = bulkSearch.trim().toLowerCase()
    const shown = records.records.filter((r) => BULK_FILTERS[bulkFilter].has(r.status)
      && (!q || Object.values(r.header || {}).some((v) => v != null && String(v).toLowerCase().includes(q))))
    // Leaving the open record with unapplied edits keeps today's question.
    const pick = (id: string) => {
      if (id === editingId) return
      if (formDirty && !window.confirm('You have unsaved field edits. Discard?')) return
      setFormDirty(false)
      startEdit(id)
    }
    const addOne = () => {
      if (formDirty && !window.confirm('You have unsaved field edits. Discard?')) return
      setFormDirty(false)
      void startAdd()
    }
    const formsForTabs = [{ formNo: mainFormNo, sm }, ...detailForms]
    const activeBulkForm = formsForTabs.some((f) => f.formNo === bulkFormTab) ? bulkFormTab : mainFormNo
    const childSection = (d: { formNo: string; sm: SqlModel }, variantKind: 'grid' | 'compact') => (
      <ChildFormTab
        key={d.formNo}
        variant={variantKind}
        project={project}
        objName={objName}
        formNo={d.formNo}
        sm={d.sm}
        parentSm={sm}
        parentPk={childParentPk}
        parentPkKey={childParentPkKey}
        parentHeaderValues={formValues}
        gridLayout={gridLayout}
        model={model!}
        depth={1}
        store={makeEmbedChildStore(d.formNo)}
        readOnly={!canEdit || formLinesViewOnly(d.sm)}
        onAncestorPatch={applyHeaderPatch}
      />
    )
    const headerForm = editingRecord ? (
      <CaptureFormRenderer
        model={sm}
        values={formValues}
        errors={editingRecord.fieldErrors}
        onChange={onFieldChange}
        readOnlyKey={!!editingRecord.primaryKey}
        project={project}
        objName={objName}
        dynamicProtect={dynamicProtect}
        uploadDocument={api.makeUploader(dataAccess, project)}
        runAction={(p, o, req) => api.runAction(dataAccess, p, o, req)}
        formNo={sm.form_no}
        txnId={editingRecord.primaryKey || undefined}
        onActionResult={(res) => applyActionResultViaOnChange(res, sm.form_no || '1', onFieldChange)}
        onActionError={(err) => notify.error(parseApiError(err).message)}
        {...(editorLayout === 'compact' ? { layout: 'compact' as const } : {})}
      />
    ) : null
    return (
      <PeopleProvider value={records.people}>
      <TxnRuntimeProvider value={txn}>
      <div style={{ display: 'flex', flexDirection: 'column' }} data-tw-view="bulk-entry">
        {/* The screen's one toolbar row: the search scope (shared with the
            List) and a search over the edit set's cards. */}
        <ListToolbar
          scope={bulkScope.scope}
          chips={bulkScope.chips}
          search={bulkSearch}
          onSearch={setBulkSearch}
          trailing={viewSwitcher}
        />
        {bulkScope.below}
        {/* The edit set's band: change chips · sync · Refresh · Discard ·
            Add · Save All (N). */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 20px',
          borderBottom: '1px solid var(--color-border, #dde3ea)',
          background: 'var(--color-surface-2, #f0f3f7)',
        }}>
          <div role="group" aria-label="Edit set — click to filter" style={{
            display: 'inline-flex', border: '1px solid var(--color-border-strong, #c7cfd9)', borderRadius: 8,
            overflow: 'hidden', background: 'var(--color-bg-secondary, #ffffff)',
          }}>
            {(Object.keys(BULK_FILTERS) as BulkFilter[]).map((k, i) => {
              const on = bulkFilter === k
              const n = records.records.filter((r) => BULK_FILTERS[k].has(r.status)).length
              if (k === 'saved' && n === 0 && !on) return null
              return (
                <button key={k} type="button" aria-pressed={on} onClick={() => setBulkFilter(k)}
                        {...tw('action', `bulk-filter:${k}`)}
                        style={{
                          height: 32, padding: '0 11px', border: 0,
                          borderLeft: i ? '1px solid var(--color-border, #dde3ea)' : 0,
                          background: on ? 'var(--color-text, #1c2430)' : 'transparent',
                          color: on ? 'var(--color-bg-secondary, #ffffff)' : BULK_FILTER_FG[k],
                          fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
                          display: 'inline-flex', gap: 5, alignItems: 'center', fontFamily: 'inherit',
                        }}>
                  {BULK_FILTER_LABEL[k]} <strong style={{ fontWeight: 700 }}>{n}</strong>
                </button>
              )
            })}
          </div>
          <SyncStatus inSync={!records.hasPendingChanges} counts={records.counts} />
          <span style={{ flex: '1 1 10px' }} />
          <button type="button" onClick={handleRetrieve}
                  style={{ ...bulkBtn, opacity: records.busy || !ready || records.hasPendingChanges ? 0.55 : 1 }}
                  disabled={records.busy || !ready || records.hasPendingChanges}
                  title={records.hasPendingChanges
                    ? 'Local changes are not synced — Save All or Discard them before refreshing'
                    : 'Reload all rows from the server'}>
            <RefreshCw size={14} /> Refresh
          </button>
          {records.hasPendingChanges && (
            <button type="button" style={{ ...bulkBtn, color: 'var(--color-error, #b42318)' }} onClick={handleDiscardChanges}
                    disabled={records.busy || !ready}
                    title="Drop all pending changes and reload from the server">
              <Trash2 size={14} /> Discard changes
            </button>
          )}
          {beforeAddActions}
          {canAdd && (
            <button type="button" style={bulkBtn} onClick={addOne}
                    disabled={records.busy || !ready} {...tw('action', 'add')}>
              <FilePlus size={14} /> Add
            </button>
          )}
          {(canAdd || canEdit) && (
            <button type="button" onClick={handleSaveAll}
                    style={{ ...bulkPrimary, opacity: records.busy || !ready || pendingCount === 0 ? 0.55 : 1 }}
                    disabled={records.busy || !ready || pendingCount === 0}
                    {...tw('action', 'save_all')}>
              <Save size={14} /> Save All{pendingCount > 0 ? ` (${pendingCount})` : ''}
            </button>
          )}
          {headerActions}
        </div>
        {records.hasPendingChanges && (
          <div style={{ ...bulkNoticeStyle, background: 'var(--color-warning-weak)', borderColor: 'var(--color-warning)' }}>
            Nothing in this edit set is saved yet. Refresh is disabled until you{' '}
            <strong>Save All</strong> or <strong>Discard changes</strong>.
          </div>
        )}
        {records.lastSaveError && (
          <div role="alert" style={{ ...bulkNoticeStyle, background: 'var(--color-error-weak)', borderColor: 'var(--color-error)' }}>
            {records.lastSaveError}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start', padding: '14px 20px 18px' }}>
          {/* The edit set, as compact cards (list_view.columns). */}
          <div style={{ flex: '1 1 300px', maxWidth: 380, minWidth: 0, border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px',
              background: 'var(--color-bg-secondary)', borderBottom: '1px solid var(--color-border)',
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
            }}>
              <strong style={{ fontWeight: 600, color: 'var(--color-text)' }}>Edit set</strong>
            </div>
            <div role="list" style={{ maxHeight: 560, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              {shown.length === 0 ? (
                <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
                  {(records.busy || !ready) ? 'Loading…'
                    : canAdd ? 'Nothing here. Refresh to pull records, or Add a new one.'
                      : 'Nothing here. Refresh to pull records.'}
                </div>
              ) : shown.map((rec) => {
                const on = rec.id === editingId
                const err = rec.recordError || Object.values(rec.fieldErrors || {})[0]
                const tag = BULK_TAG[rec.status]
                return (
                  <div key={rec.id} role="listitem"
                       {...tw('row', rec.primaryKey || rec.id, { status: rec.status })}
                       style={{ display: 'flex', alignItems: 'flex-start', gap: 4, padding: '4px 6px 4px 4px',
                         borderBottom: '1px solid var(--color-border, #dde3ea)',
                         background: on ? 'var(--color-accent-weak, rgba(0,97,208,0.10))' : 'transparent' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CompactSummaryCard
                        flat
                        sm={sm}
                        row={rec.header}
                        selected={false}
                        onClick={() => (onSetInput ? (handleSelect(rec), pick(rec.id)) : pick(rec.id))}
                        lead={<span style={{ ...bulkTagStyle, background: tag.bg, color: tag.fg }}>{tag.label}</span>}
                        footer={err ? (
                          <span style={{ fontSize: '0.8125rem', color: 'var(--color-error, #b42318)' }}>{String(err)}</span>
                        ) : undefined}
                      />
                    </div>
                    <button type="button"
                            aria-label="Remove from this edit set"
                            title="Remove from this edit set — nothing is deleted"
                            onClick={(e) => { e.stopPropagation(); handleRemoveLocal(rec.id) }}
                            style={{ marginTop: 8, width: 30, height: 30, border: 0, borderRadius: 8,
                              background: 'transparent', color: 'var(--color-text-secondary, #465062)',
                              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X size={15} />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>

          {/* The selected record in the record editor. */}
          {editingRecord && (
            <div data-tw-dialog="editor" data-tw-mode={editingRecord.primaryKey ? 'edit' : 'add'}
                 style={{ flex: '999 1 540px', minWidth: 0, border: '1px solid var(--color-border)', borderRadius: 10, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '12px 18px', borderBottom: '1px solid var(--color-border)' }}>
                <strong style={{ fontSize: '1rem' }}>
                  {editingRecord.primaryKey ? `Edit ${sm.form_title || objName}` : `New ${sm.form_title || objName}`}
                </strong>
                <span style={{ ...bulkTagStyle, background: BULK_TAG[editingRecord.status].bg, color: BULK_TAG[editingRecord.status].fg }}>
                  {BULK_TAG[editingRecord.status].label}
                </span>
                {editingRecord.primaryKey && (
                  <code style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{editingRecord.primaryKey}</code>
                )}
                {formDirty && (
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-warning)' }}>
                    ● Unsaved field edits
                  </span>
                )}
                <span style={{ flex: '1 1 10px' }} />
                {editorLayoutSwitch}
              </div>
              {editorLayout === 'standard' && formsForTabs.length > 1 && (
                <FormTabs forms={formsForTabs} active={activeBulkForm}
                          onChange={setBulkFormTab} childTabsEnabled={true} />
              )}
              {(editingRecord.recordError || Object.keys(editingRecord.fieldErrors).length > 0) && (
                <ValidationBanner
                  fieldErrors={editingRecord.fieldErrors}
                  banner={editingRecord.recordError}
                  columnLabel={(dbName) => columnLabelFromSm(sm, dbName)}
                />
              )}
              <div style={{
                padding: '8px 0', width: '100%', boxSizing: 'border-box', alignSelf: 'center',
                maxWidth: editorLayout === 'compact' ? 520 : undefined,
              }}>
                {editorLayout === 'standard' ? (
                  activeBulkForm === mainFormNo
                    ? headerForm
                    : model && formsForTabs.filter((f) => f.formNo === activeBulkForm)
                      .map((d) => childSection(d, 'grid'))
                ) : (
                  <>
                    {headerForm}
                    {model && detailForms.map((d) => (
                      <div key={d.formNo} style={{ margin: '0 14px 12px' }}>
                        {editorLayout === 'simple' && (
                          <div style={{ fontWeight: 600, fontSize: '0.8125rem', padding: '6px 0' }}>
                            {d.sm.form_title || `Detail ${d.formNo}`}
                          </div>
                        )}
                        {childSection(d, editorLayout === 'compact' ? 'compact' : 'grid')}
                      </div>
                    ))}
                  </>
                )}
              </div>
              <div style={{
                marginTop: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
                padding: '12px 18px', borderTop: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)', borderRadius: '0 0 10px 10px',
              }}>
                <span style={{ flex: '1 1 220px', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  Apply keeps this record in the edit set. Nothing is saved until Save All.
                </span>
                <button type="button" style={bulkBtn} onClick={closeEditor}
                        {...tw('action', 'cancel')}>Cancel</button>
                <button type="button" style={bulkPrimary} disabled={records.busy}
                        {...tw('action', 'apply')}
                        onClick={() => { applyFormToRecord(); userClosedRef.current = true; setEditorOpen(false); setEditingId(null) }}>
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
        {warningsModal}
      </div>
      </TxnRuntimeProvider>
      </PeopleProvider>
    )
  }

  return (
    // The core form / lookup modal read their transport from the TxnRuntime
    // context — provide the host's, exactly as the platform's per-component
    // wrappers used to (CaptureFormRenderer overlays upload/run_action).
    // PeopleProvider names the user keys in the retrieved rows, so an embedded
    // grid reads a person where the full screen does (see `txn/people`).
    <PeopleProvider value={records.people}>
    <TxnRuntimeProvider value={txn}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginRight: 'auto' }}>
          <SyncStatus
            inSync={!records.hasPendingChanges}
            counts={records.counts}
          />
          {deployState === 'deploying' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              deploying…
            </span>
          )}
          {deployState === 'error' && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-error, #b91c1c)' }}>
              deploy failed: {deployError}
            </span>
          )}
        </div>
        <LayoutActions
          show={canEditDesign && layoutIsDirty(gridLayout.layouts[mainFormNo])}
          applying={gridLayout.applyingFormNo === mainFormNo}
          onReset={() => gridLayout.reset(mainFormNo)}
          onApply={() => gridLayout.apply(mainFormNo)}
          applyLabel={gridLayout.applyLabel}
          applyTitle={gridLayout.applyTitle}
        />
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={handleRetrieve}
          disabled={records.busy || deployState !== 'deployed' || records.hasPendingChanges}
          title={
            records.hasPendingChanges
              ? 'Local changes are not synced — Save All or Discard them before refreshing'
              : 'Reload all rows from the server'
          }
        >
          <RefreshCw size={14} /> Refresh
        </button>
        {records.hasPendingChanges && (
          <button
            type="button" className="btn btn-ghost btn-sm"
            onClick={handleDiscardChanges}
            disabled={records.busy || deployState !== 'deployed'}
            title="Drop all pending changes and reload from the server"
            style={{ color: '#b91c1c' }}
          >
            <Trash2 size={14} /> Discard changes
          </button>
        )}
        {canAdd && (
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={startAdd}
          disabled={records.busy || deployState !== 'deployed'}
        >
          <FilePlus size={14} /> Add
        </button>
        )}
        {(canAdd || canEdit) && (
        <button
          type="button" className="btn btn-primary btn-sm"
          onClick={handleSaveAll}
          disabled={
            records.busy ||
            deployState !== 'deployed' ||
            (records.counts.unsaved + records.counts.dirty +
             records.counts.failed + records.counts.partial) === 0
          }
        >
          <Save size={14} /> Save All
          {(records.counts.unsaved + records.counts.dirty +
            records.counts.failed + records.counts.partial) > 0 && (
            <span style={{ marginLeft: 4, opacity: 0.85 }}>
              ({records.counts.unsaved + records.counts.dirty +
                records.counts.failed + records.counts.partial})
            </span>
          )}
        </button>
        )}
        {viewSwitcher}
      </div>

      {records.hasPendingChanges && (
        <div style={{
          padding: '6px 10px', fontSize: '0.9125rem',
          background: 'rgba(234,179,8,0.10)',
          border: '1px solid rgba(234,179,8,0.40)',
          borderRadius: 6, color: '#a16207',
        }}>
          Local changes are not synced with the server. Refresh is
          disabled until you <strong>Save All</strong> or{' '}
          <strong>Discard changes</strong>.
        </div>
      )}

      {records.lastSaveError && (
        <div style={{
          padding: '6px 10px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.35)',
          borderRadius: 6, color: '#b91c1c',
          fontSize: '0.9125rem',
        }}>{records.lastSaveError}</div>
      )}

      {/* List */}
      {compact ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {records.records.length === 0 ? (
            <div style={{
              padding: '24px 16px', textAlign: 'center',
              color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)', borderRadius: 12,
            }}>
              {(records.busy || deployState !== 'deployed')
                ? 'Loading…'
                : canAdd
                  ? 'No records yet. Tap Refresh to pull rows, or Add for a new one.'
                  : 'No records yet. Tap Refresh to pull rows.'}
            </div>
          ) : records.records.map((rec) => (
            <CompactRecordCard
              key={rec.id}
              sm={sm}
              row={rec.header}
              onClick={() => (onSetInput ? handleSelect(rec) : startEdit(rec.id))}
              trailing={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <StatusBadge status={rec.status} />
                  <RowActionsMenu
                    actions={[
                      { label: 'Edit', icon: <Pencil size={14} />, onClick: () => startEdit(rec.id) },
                      { label: 'Remove', icon: <Trash2 size={14} />, onClick: () => handleRemoveLocal(rec.id), danger: true },
                    ]}
                  />
                </span>
              }
            />
          ))}
        </div>
      ) : (
      <RecordList
        sm={sm}
        records={records.records}
        selectedId={selectedId}
        busy={records.busy || deployState !== 'deployed'}
        gridLayout={gridLayout}
        formNo={mainFormNo}
        canAdd={canAdd}
        onSelect={handleSelect}
        onEdit={startEdit}
        onRemove={handleRemoveLocal}
      />
      )}

      {/* Editor modal (compact = full-screen single-column form) */}
      {compact ? (
        <CompactFormShell
          open={editorOpen}
          title={
            editingRecord?.primaryKey
              ? `Edit ${sm.form_title || objName}`
              : `New ${sm.form_title || objName}`
          }
          mode={editingRecord?.primaryKey ? 'edit' : 'add'}
          txnId={editingRecord?.primaryKey}
          dirty={formDirty}
          busy={records.busy}
          onRequestClose={closeEditor}
          onSave={() => {
            applyFormToRecord()
            setEditorOpen(false)
            setEditingId(null)
          }}
        >
          {editingRecord && (
            <>
              {(editingRecord.recordError || Object.keys(editingRecord.fieldErrors).length > 0) && (
                <ValidationBanner
                  fieldErrors={editingRecord.fieldErrors}
                  banner={editingRecord.recordError}
                  columnLabel={(dbName) => columnLabelFromSm(sm, dbName)}
                />
              )}
              <CaptureFormRenderer
                model={sm}
                values={formValues}
                errors={editingRecord.fieldErrors}
                onChange={onFieldChange}
                readOnlyKey={!!editingRecord.primaryKey}
                project={project}
                objName={objName}
                dynamicProtect={dynamicProtect}
                uploadDocument={api.makeUploader(dataAccess, project)}
                runAction={(p, o, req) => api.runAction(dataAccess, p, o, req)}
                formNo={sm.form_no}
                txnId={editingRecord.primaryKey || undefined}
                onActionResult={(res) => applyActionResultViaOnChange(res, sm.form_no || '1', onFieldChange)}
                onActionError={(err) => notify.error(parseApiError(err).message)}
                layout="compact"
              />
              {/* Direct child forms as stacked compact sections (backed by the
                  record's details buffer; persisted with the header on Save All). */}
              {detailForms.length > 0 && model && (
                <TxnScreenRuntimeProvider value={screenRuntimeAdapter}>
                  {detailForms.map((d) => (
                    <ChildFormTab
                      key={d.formNo}
                      variant="compact"
                      project={project}
                      objName={objName}
                      formNo={d.formNo}
                      sm={d.sm}
                      parentSm={sm}
                      parentPk={childParentPk}
                      parentPkKey={childParentPkKey}
                      parentHeaderValues={formValues}
                      gridLayout={gridLayout}
                      model={model}
                      depth={1}
                      store={makeEmbedChildStore(d.formNo)}
                      readOnly={!canEdit || formLinesViewOnly(d.sm)}
                      onAncestorPatch={applyHeaderPatch}
                    />
                  ))}
                </TxnScreenRuntimeProvider>
              )}
            </>
          )}
        </CompactFormShell>
      ) : (
      <EditorModal
        open={editorOpen}
        title={
          editingRecord?.primaryKey
            ? `Edit ${sm.form_title || objName}`
            : `New ${sm.form_title || objName}`
        }
        record={editingRecord}
        formDirty={formDirty}
        busy={records.busy}
        onApply={() => {
          applyFormToRecord()
          setEditorOpen(false)
          setEditingId(null)
        }}
        onClose={closeEditor}
      >
        {editingRecord && (
          <>
            {(editingRecord.recordError || Object.keys(editingRecord.fieldErrors).length > 0) && (
              <ValidationBanner
                fieldErrors={editingRecord.fieldErrors}
                banner={editingRecord.recordError}
                columnLabel={(dbName) => columnLabelFromSm(sm, dbName)}
              />
            )}
            <div style={{ flex: 1, overflow: 'auto' }}>
              <CaptureFormRenderer
                model={sm}
                values={formValues}
                errors={editingRecord.fieldErrors}
                onChange={onFieldChange}
                readOnlyKey={!!editingRecord.primaryKey}
                project={project}
                objName={objName}
                dynamicProtect={dynamicProtect}
                uploadDocument={api.makeUploader(dataAccess, project)}
                runAction={(p, o, req) => api.runAction(dataAccess, p, o, req)}
                formNo={sm.form_no}
                txnId={editingRecord.primaryKey || undefined}
                onActionResult={(res) => applyActionResultViaOnChange(res, sm.form_no || '1', onFieldChange)}
                onActionError={(err) => notify.error(parseApiError(err).message)}
              />
            </div>
          </>
        )}
      </EditorModal>
      )}
      {/* "Ignore warnings and proceed" confirm for save warnings. */}
      {warningsModal}
    </div>
    </TxnRuntimeProvider>
    </PeopleProvider>
  )
}

// Bulk entry's change filter: All · New · Edited · Unchanged · Saved · Fix.
type BulkFilter = 'all' | 'new' | 'edited' | 'unchanged' | 'saved' | 'fix'
const BULK_FILTERS: Record<BulkFilter, Set<RecordStatus>> = {
  all: new Set<RecordStatus>(['retrieved', 'dirty', 'unsaved', 'saved', 'failed', 'partial']),
  new: new Set<RecordStatus>(['unsaved']),
  edited: new Set<RecordStatus>(['dirty']),
  unchanged: new Set<RecordStatus>(['retrieved']),
  saved: new Set<RecordStatus>(['saved']),
  fix: new Set<RecordStatus>(['failed', 'partial']),
}
const BULK_FILTER_LABEL: Record<BulkFilter, string> = {
  all: 'All', new: 'New', edited: 'Edited', unchanged: 'Unchanged', saved: 'Saved', fix: 'Fix',
}
// The record's change tag in Bulk entry (the Spread sheet's words and tones).
const BULK_TAG: Record<RecordStatus, { label: string; bg: string; fg: string }> = {
  retrieved: { label: 'Unchanged', bg: 'rgba(100,116,139,0.14)', fg: '#465062' },
  dirty: { label: 'Edited', bg: 'rgba(217,138,18,0.16)', fg: '#7a4300' },
  unsaved: { label: 'New', bg: 'rgba(59,130,246,0.14)', fg: '#0b4a96' },
  saved: { label: 'Saved', bg: 'rgba(16,185,129,0.16)', fg: '#05603a' },
  failed: { label: 'Failed', bg: 'rgba(217,45,32,0.12)', fg: '#b42318' },
  partial: { label: 'Partial', bg: 'rgba(217,45,32,0.12)', fg: '#b42318' },
}
const BULK_FILTER_FG: Record<BulkFilter, string> = {
  all: 'var(--color-text, #1c2430)', new: '#0b4a96', edited: '#7a4300',
  unchanged: 'var(--color-text, #1c2430)', saved: '#05603a', fix: '#b42318',
}
const bulkTagStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 7px', borderRadius: 6,
  fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
}
const bulkBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', borderRadius: 8,
  border: '1px solid var(--color-border-strong, #c7cfd9)', background: 'var(--color-bg-secondary, #ffffff)',
  color: 'var(--color-text, #1c2430)', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit', whiteSpace: 'nowrap',
}
const bulkPrimary: React.CSSProperties = {
  ...bulkBtn, border: '1px solid transparent', background: 'var(--color-accent, #0061D0)', color: '#ffffff', fontWeight: 600,
}
const bulkNoticeStyle: React.CSSProperties = {
  margin: '12px 20px 0', padding: '8px 12px', borderRadius: 8, border: '1px solid',
  color: 'var(--color-text)', fontSize: '0.8125rem',
}

// ── Sub-components ─────────────────────────────────────

// Width column for the Status badge cell + the trailing actions cell.
// Status fits the badge comfortably; actions hold Edit + Trash icon.
const STATUS_COL_WIDTH = 90
const ACTIONS_COL_WIDTH = 110

function RecordList({
  sm, records, selectedId, busy, gridLayout, formNo, canAdd = true, onSelect, onEdit, onRemove,
}: {
  sm: SqlModel
  records: EmbedRecord[]
  selectedId: string | null
  busy: boolean
  gridLayout?: GridLayoutApi
  formNo?: string
  /** Gate the empty-state's "+ Add" hint (read-only viewers can't add). */
  canAdd?: boolean
  onSelect: (rec: EmbedRecord) => void
  onEdit: (id: string) => void
  onRemove: (id: string) => void
}) {
  // Show every visible column in the designer's grid order (x position), with
  // any live width/order override applied (drag to resize / reorder). Don't
  // truncate columns — capture mode mirrors what the author drew.
  const baseCols = useMemo(() => gridOrderedColumns(sm), [sm])
  // A user-key column reads as the person it points at (see `txn/people`).
  const people = usePeople()
  const rl = useResolvedGridLayout(gridLayout, formNo)
  const cols = useMemo(() => orderedDisplayCols(baseCols, rl.layout), [baseCols, rl.layout])
  const wOf = useCallback((c: ColumnDef) => widthOf(c, rl.layout), [rl.layout])
  const dnd = useColumnDnd({
    displayKeys: useMemo(() => cols.map(colKeyOf), [cols]),
    enabled: true,
    onReorder: rl.setOrder,
    onResize: rl.setWidth,
  })

  if (records.length === 0) {
    return (
      <div style={{
        padding: '24px 16px', textAlign: 'center',
        color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)', borderRadius: 8,
      }}>
        {busy
          ? 'Loading…'
          : canAdd
            ? 'No records yet. Click Refresh to pull existing rows, or + Add for a new one.'
            : 'No records yet. Click Refresh to pull existing rows.'}
      </div>
    )
  }

  const tableWidth = cols.reduce((sum, c) => sum + wOf(c),
    STATUS_COL_WIDTH + ACTIONS_COL_WIDTH)

  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 8,
      maxHeight: 360, overflow: 'auto', background: 'var(--color-bg)',
    }}>
      <table style={{
        borderCollapse: 'collapse', fontSize: '0.9375rem',
        // ``fixed`` layout makes the browser honour the <colgroup>
        // widths instead of distributing based on content.
        tableLayout: 'fixed',
        width: tableWidth,
      }}>
        <colgroup>
          <col style={{ width: STATUS_COL_WIDTH }} />
          {cols.map((c) => (
            <col
              key={c.col_id || columnDbName(c)}
              data-col-key={colKeyOf(c)}
              style={{ width: wOf(c) }}
            />
          ))}
          <col style={{ width: ACTIONS_COL_WIDTH }} />
        </colgroup>
        <thead>
          <tr>
            <th style={thStyle}>Status</th>
            {cols.map((c) => {
              const ck = colKeyOf(c)
              const ind = dnd.dropIndicator?.key === ck ? dnd.dropIndicator.side : null
              return (
                <th
                  key={c.col_id || columnDbName(c)}
                  {...dnd.getHeaderProps(ck)}
                  title="Drag to reorder · drag the right edge to resize"
                  style={{
                    ...thStyle,
                    cursor: 'grab',
                    boxShadow: ind === 'left'
                      ? 'inset 3px 0 0 var(--color-accent)'
                      : ind === 'right'
                        ? 'inset -3px 0 0 var(--color-accent)'
                        : undefined,
                  }}
                >
                  {listHeadingOf(c) ?? columnDbName(c)}
                  <div {...dnd.getResizeHandleProps(ck)} style={RESIZE_HANDLE_STYLE} />
                </th>
              )
            })}
            <th style={{ ...thStyle, textAlign: 'right' }}></th>
          </tr>
        </thead>
        <tbody>
          {records.map((rec) => {
            const isSel = rec.id === selectedId
            return (
              <tr
                key={rec.id}
                onClick={() => onSelect(rec)}
                onDoubleClick={() => onEdit(rec.id)}
                style={{
                  cursor: 'pointer',
                  background: isSel ? 'rgba(99,102,241,0.10)' : undefined,
                  borderBottom: '1px solid var(--color-border)',
                }}
              >
                <td style={tdStyle}><StatusBadge status={rec.status} /></td>
                {cols.map((c) => (
                  <td key={c.col_id || columnDbName(c)} style={tdStyle}>
                    {/* A record's header is form-state, keyed by form key —
                        db_name misses a joined-master column whose db_name
                        differs from its name (see ../list/ListView). */}
                    {personLabel(people, columnFormKey(c), rec.header[columnFormKey(c)])
                      || formatCell(rec.header[columnFormKey(c)], c)}
                  </td>
                ))}
                <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEdit(rec.id) }}
                    className="btn btn-ghost btn-xs"
                    title="Edit"
                  >Edit</button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(rec.id) }}
                    className="btn btn-ghost btn-xs"
                    title="Remove from local edit set"
                    style={{ marginLeft: 4 }}
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function formatCell(v: unknown, col?: ColumnDef): string {
  if (v === null || v === undefined || v === '') return '—'
  const rich = richCellText(v, col)
  if (rich != null) return rich || '—'
  // ddlb / radio / checkbox / bool_check: show the display label, not the
  // raw data code (e.g. "Classical" instead of "CLASSICAL").
  if (col) {
    const label = ddlbDisplayLabel(v, col)
    if (label != null) return label
  }
  if (typeof v === 'object') {
    try { return JSON.stringify(v) } catch { return String(v) }
  }
  return String(v)
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontWeight: 600,
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  background: 'var(--color-surface-2, var(--color-bg-secondary))',
  borderBottom: '1px solid var(--color-border)',
  position: 'sticky', top: 0, zIndex: 1,
}
const tdStyle: React.CSSProperties = {
  padding: '6px 10px', color: 'var(--color-text)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}

// ── Modal helpers (slimmer than LivePreviewTab) ────────

function EditorModal({
  open, title, record, formDirty, busy,
  onApply, onClose, children,
}: {
  open: boolean
  title: string
  record: EmbedRecord | null
  formDirty: boolean
  busy: boolean
  onApply: () => void
  onClose: () => void
  children: React.ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  const node = (
    <div
      onClick={onClose}
      style={{
        // Above the Run view's sticky top bar (zIndex 9000) so the editor
        // header (Close / Save) isn't truncated behind it — matches the
        // standalone LivePreviewTab editor (9430). Lookup/child modals (9440+)
        // still stack above this.
        position: 'fixed', inset: 0, zIndex: 9430,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg)', color: 'var(--color-text)',
          border: '1px solid var(--color-border)', borderRadius: 10,
          width: 'min(1000px, 96vw)', height: 'min(760px, 92vh)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)', overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 18px', borderBottom: '1px solid var(--color-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ fontSize: '0.9625rem' }}>{title}</strong>
            {record && <StatusBadge status={record.status} />}
            {record?.primaryKey && (
              <code style={{
                fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                background: 'var(--color-bg-secondary)',
                padding: '2px 6px', borderRadius: 3,
              }}>
                {record.primaryKey.slice(0, 18)}{record.primaryKey.length > 18 ? '…' : ''}
              </code>
            )}
            {formDirty && (
              <span style={{ fontSize: '0.8125rem', color: '#eab308', fontWeight: 600 }}>
                ● unsaved field edits
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>
              <X size={14} /> Close
            </button>
            <button className="btn btn-primary btn-sm" onClick={onApply} disabled={busy}>
              <FilePlus size={14} /> Apply
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      </div>
    </div>
  )
  return createPortal(node, document.body)
}

function ValidationBanner({
  fieldErrors, banner, columnLabel,
}: {
  fieldErrors: Record<string, string>
  banner: string | null
  columnLabel: (dbName: string) => string
}) {
  const keys = Object.keys(fieldErrors)
  if (keys.length === 0 && !banner) return null
  return (
    <div style={{
      padding: '10px 16px',
      background: 'rgba(239,68,68,0.08)',
      borderBottom: '1px solid rgba(239,68,68,0.35)',
      color: '#ef4444', fontSize: '0.9125rem',
    }}>
      <div style={{ fontWeight: 600, marginBottom: keys.length ? 4 : 0 }}>
        {keys.length > 0
          ? `Save blocked — ${keys.length} validation error${keys.length === 1 ? '' : 's'}`
          : banner}
      </div>
      {keys.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {keys.map((k) => (
            <li key={k}>
              <a
                href={`#${fieldAnchorId(k)}`}
                onClick={(e) => {
                  e.preventDefault()
                  const el = document.getElementById(fieldAnchorId(k))
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }}
                style={{ color: 'inherit' }}
              >
                <strong>{columnLabel(k)}</strong>: {fieldErrors[k]}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function columnLabelFromSm(sm: SqlModel | null, key: string): string {
  if (!sm?.columns) return key
  // ``key`` may be a form_key (modern validation) or a db_name (PG-error
  // fallback). Match against both shapes.
  for (const wrap of sm.columns) {
    const col = wrap.column as ColumnDef | undefined
    if (!col) continue
    const fkey = (col.name || col.db_name || '').replace(/\s+/g, '_').toLowerCase()
    const dbn = (col.db_name || col.name || '').replace(/\s+/g, '_').toLowerCase()
    if (fkey === key || dbn === key) {
      return col.heading || col.name || key
    }
  }
  return key
}

const emptyStyle: React.CSSProperties = {
  padding: '20px 14px',
  display: 'flex', alignItems: 'center', gap: 10,
  background: 'var(--color-bg)',
  border: '1px dashed var(--color-border)', borderRadius: 8,
  color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
}
